import { Alert, Badge, Button, buttonClass, cardClass, fieldClass, selectClass, Table, textareaClass } from '@ledgerloom/ui';
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, propose, type Candidate, type Party } from '../api';
import { useApi, usePageTitle } from '../hooks';
import { useSession } from '../session';
import { Avatar, Breadcrumb, Empty, ErrorBanner, Icon, Loading, outcomeMessage, outcomeTone, Score, Time, ToggleGroup, useToast } from '../components/ui';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { Tabs } from '../components/Tabs';
import { forgetParty, isHiddenParty, PartyName } from '../components/PartyName';
import { InlineGroup, InlineSelect, InlineTags, InlineText, type Save } from '../components/InlineEdit';
import { day, human, money, systemName } from '../format';
import { gradeName, lifecycleOf, NO_STAGE, type Fields } from '../contactSettings';

const REL_LABEL: Record<string, [string, string]> = {
  household_member: ['Member of', 'Household member'],
  employee_of: ['Works at', 'Works here'],
  referred_by: ['Referred by', 'Referred by this contact'],
  spouse: ['Spouse', 'Spouse'],
  account_role: ['Plays a role for', 'Account role held by'],
};

function formatAddress(v: Record<string, string>) {
  return [v.street, [v.city, v.state].filter(Boolean).join(', '), v.postal_code].filter(Boolean).join(' · ');
}

interface Timeline {
  leads: Array<{ id: string; title: string; status: string; score: number | null; grade: string | null; created_at: string }>;
  deals: Array<{ id: string; title: string; status: string; stage_name: string; value_minor: string; currency: string; expected_close: string | null }>;
  activities: Array<{ id: string; kind: string; subject: string; status: string; due_at: string | null; done_at: string | null; created_at: string }>;
}
interface Thread { id: string; subject: string; status: string; mailbox: string; last_message_at: string; messages: number; last_direction: string | null }
interface Related {
  quotes: Array<{ id: string; number: string; version: number; status: string; total_minor: string; currency: string; deal_id: string | null; created_at: string }>;
  enrollments: Array<{ id: string; sequence_id: string; sequence_name: string; status: string; enrolled_at: string }>;
  segments: Array<{ id: string; name: string; added_at: string }>;
  events: Array<{ id: string; title: string; kind: string; starts_at: string; ends_at: string; calendar_id: string }>;
}
type Action = 'note' | 'email' | 'call' | 'task' | 'meeting';
type Tab = 'overview' | 'activities' | 'about' | 'revenue' | 'intelligence';
const TABS: Array<[Tab, string]> = [['overview', 'Overview'], ['activities', 'Activities'], ['about', 'About'], ['revenue', 'Revenue'], ['intelligence', 'Intelligence']];

// One contact's command center (docs/design/contacts.md): who they are and what
// you can do on the left, everything known in tabs in the middle, and the
// records beside them on the right, all without leaving the page. Patterns:
// navigation/tabs, data-display/timeline, content-management/modal for quick
// actions and a merge, data-display/comparison-table for duplicates, and
// forms/text-field and forms/selection-input for fields edited in place.
export function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const [bump, setBump] = useState(0);
  const { data: party, error, loading, reload } = useApi<Party>(`/v1/parties/${id}`, [id]);
  const timeline = useApi<Timeline>(`/v1/parties/${id}/timeline`, [id, bump]);
  const threads = useApi<Thread[]>(`/v1/threads?party=${id}&limit=50`, [id, bump]);
  const related = useApi<Related>(`/v1/parties/${id}/related`, [id, bump]);
  const fields = useApi<Fields>('/v1/contact-fields');
  const users = useApi<User[]>('/v1/users');
  const [action, setAction] = useState<Action | null>(null);
  const save = useSaver(party, () => { if (party) forgetParty(party.id); void reload(); });
  usePageTitle(party?.display_name);
  const tab = (TABS.some(([t]) => t === params.get('tab')) ? params.get('tab') : 'overview') as Tab;

  if (loading && !party) return <Loading label="Loading contact" />;
  if (error || !party) return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/contacts', label: 'Contacts' }, { label: 'Not available' }]} />
      <ErrorBanner error={error ?? new Error('This contact was not found.')} />
      <div className="row"><Button type="submit" variant="secondary" size="sm" onClick={() => void reload()}>Try again</Button><Link className={buttonClass({ variant: 'ghost', size: 'sm' })} to="/contacts">Back to contacts</Link></div>
    </div>
  );

  const refresh = () => { forgetParty(party.id); void reload(); setBump((b) => b + 1); };
  const active = party.status === 'active';
  const email = party.contact_points.find((c) => c.kind === 'email');
  const company = party.relationships.find((r) => r.kind === 'employee_of' && r.direction === 'out');
  const ctx: Ctx = { party, timeline: timeline.data, threads: threads.data, related: related.data, fields: fields.data, users: users.data ?? [], refresh, save, editable: active };

  return (
    <div className="stack command-center">
      <Breadcrumb items={[{ to: '/contacts', label: 'Contacts' }, { label: party.display_name }]} />
      {party.merged_into_id && <Alert role="note" tone="info">This record was merged into <PartyName id={party.merged_into_id} />. Everything it held moved there.</Alert>}
      <div className="cc-grid">
        <aside className="cc-left stack" aria-label="Contact summary">
          <div className={`${cardClass} card-pad stack`} style={{ gap: 12 }}>
            <div className="row" style={{ gap: 12, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
              <Avatar name={party.display_name} kind={party.kind} />
              <div style={{ minWidth: 0 }}>
                <h1 className="cc-name">{party.display_name}</h1>
                {company && <div className="muted">{isHiddenParty(company.other_name) ? company.other_name : <Link to={`/contacts/${company.other_party_id}`}>{company.other_name}</Link>}</div>}
                {email && <CopyLine text={email.value.raw} />}
              </div>
            </div>
            <div className="row" style={{ gap: 6 }}>
              <Badge>{human(party.kind)}</Badge>
              {!active && <Badge tone="danger">{party.status === 'merged' ? 'Merged' : 'Archived'}</Badge>}
              {party.review_state === 'needs_review' && <Badge tone="warning">Needs review</Badge>}
            </div>
            {active && (
              <div className="quick-actions" role="group" aria-label="Quick actions">
                {([['note', 'Note', 'note'], ['email', 'Email', 'mail'], ['call', 'Call', 'phone'], ['task', 'Task', 'task'], ['meeting', 'Meeting', 'calendar']] as const).map(([a, label, icon]) => (
                  <button key={a} type="button" className="quick-action" disabled={a === 'email' && !email} title={a === 'email' && !email ? 'No email address' : undefined} onClick={() => setAction(a)}>
                    <span className="quick-icon" aria-hidden="true"><Icon name={icon} /></span>{label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <KeyInfo ctx={ctx} />
        </aside>
        <section className="cc-main" aria-label="Everything about this contact">
          <Tabs label="About this contact" value={tab} tabs={TABS} onChange={(t) => { const p = new URLSearchParams(params); if (t === 'overview') p.delete('tab'); else p.set('tab', t); setParams(p, { replace: true }); }}>
            {tab === 'overview' && <Overview ctx={ctx} onAction={active ? setAction : undefined} />}
            {tab === 'activities' && <Activities ctx={ctx} />}
            {tab === 'about' && <About ctx={ctx} />}
            {tab === 'revenue' && <Revenue ctx={ctx} />}
            {tab === 'intelligence' && <Intelligence party={party} onChanged={refresh} />}
          </Tabs>
        </section>
        <aside className="cc-right stack" aria-label="Related records">
          <RelatedCard title="Companies" count={party.relationships.filter((r) => r.kind === 'employee_of').length}>
            {party.relationships.filter((r) => r.kind === 'employee_of').map((r) => (
              <RelatedRow key={r.id} to={isHiddenParty(r.other_name) ? undefined : `/contacts/${r.other_party_id}`} title={r.other_name} sub={r.direction === 'out' ? 'Works here' : 'Works for this organization'} />
            ))}
          </RelatedCard>
          <RelatedCard title="Deals" count={timeline.data?.deals.length}>
            {timeline.data?.deals.map((d) => <RelatedRow key={d.id} to={`/deals/${d.id}`} title={d.title} sub={`${d.status === 'open' ? d.stage_name : human(d.status)} · ${money(d.value_minor, d.currency)}`} />)}
          </RelatedCard>
          <RelatedCard title="Quotes" count={related.data?.quotes.length}>
            {related.data?.quotes.map((q) => <RelatedRow key={q.id} to={`/quotes/${q.id}`} title={`${q.number} v${q.version}`} sub={`${human(q.status)} · ${money(q.total_minor, q.currency)}`} />)}
          </RelatedCard>
          <RelatedCard title="Sequences" count={related.data?.enrollments.length}>
            {related.data?.enrollments.map((e) => <RelatedRow key={e.id} to="/marketing?tab=sequences" title={e.sequence_name} sub={`${human(e.status)} · enrolled ${day(e.enrolled_at)}`} />)}
          </RelatedCard>
          <RelatedCard title="Segments" count={related.data?.segments.length}>
            {related.data?.segments.map((s) => <RelatedRow key={s.id} to="/marketing?tab=segments" title={s.name} sub={`Since ${day(s.added_at)}`} />)}
          </RelatedCard>
          {party.kind === 'person' && <Consent partyId={party.id} />}
        </aside>
      </div>
      {action && <QuickAction kind={action} party={party} onClose={() => setAction(null)} onDone={refresh} />}
    </div>
  );
}

interface User { id: string; display_name: string; status: string }
type Saver = (schema: 'party.update' | 'party.set_stage', payload: Record<string, unknown>) => Save;
interface Ctx {
  party: Party; timeline: Timeline | null; threads: Thread[] | null; related: Related | null; fields: Fields | null; users: User[];
  refresh: () => void; save: Saver; editable: boolean;
}

// Each field saves as its own proposal, one after another, each at the version
// the last one returned. A save that waits for approval says so.
function useSaver(party: Party | null, reload: () => void): Saver {
  const version = useRef(party?.version ?? 0);
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  useEffect(() => { if (party && party.version > version.current) version.current = party.version; }, [party?.version]); // eslint-disable-line react-hooks/exhaustive-deps
  return (schema, payload) => () => {
    const next = chain.current.then(async () => {
      const out = await propose(schema, { party_id: party!.id, base_version: version.current, ...payload });
      if (out.state === 'written') {
        version.current = Number(out.result?.version ?? version.current + 1);
        reload();
        return null;
      }
      if (out.state === 'pending_approval') return 'Sent for approval';
      reload();
      throw new Error(outcomeMessage(out));
    });
    chain.current = next.catch(() => undefined);
    return next as Promise<string | null>;
  };
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const checkEmail = (v: string) => (EMAIL.test(v) ? null : 'Enter an email like name@example.com.');
const checkPhone = (v: string) => (v.replace(/\D/g, '').length >= 7 ? null : 'Enter the full number, with area code.');
type Address = { street: string; city: string; state: string; postal_code: string };
const checkAddress = (a: Address) => {
  if (!Object.values(a).some((v) => v.trim())) return 'Enter at least a street or a city.';
  if (a.state.trim() && !/^[A-Za-z]{2}$/.test(a.state.trim())) return 'Use the two-letter state code, like TX.';
  if (a.postal_code.trim() && !/^\d{5}(-?\d{4})?$/.test(a.postal_code.trim())) return 'Use a 5-digit ZIP, or 5+4.';
  return null;
};
const addressSpec = (a: Address, label: string, primary: boolean) => ({
  ...Object.fromEntries(Object.entries(a).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v)), label, primary,
});
const addressOf = (v: Record<string, string>): Address => ({ street: v.street ?? '', city: v.city ?? '', state: v.state ?? '', postal_code: v.postal_code ?? '' });
const LABELS: Record<'email' | 'phone' | 'address', string[]> = { email: ['personal', 'work', 'other'], phone: ['mobile', 'home', 'work', 'other'], address: ['home', 'work', 'mailing', 'other'] };
type Point = Party['contact_points'][number];
// A contact point changes by removing it and adding the new one in the same proposal.
const pointAdd = (kind: Point['kind'], value: unknown, label: string, primary: boolean) =>
  kind === 'email' ? { emails_add: [{ address: value, label, primary }] } : kind === 'phone' ? { phones_add: [{ number: value, label, primary }] } : { addresses_add: [addressSpec(value as Address, label, primary)] };

function CopyLine({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
      <a href={`mailto:${text}`} className="wrap-text">{text}</a>
      <Button variant="ghost" size="sm" className="size-9 shrink-0 p-0" aria-label={copied ? 'Copied' : `Copy ${text}`} onClick={() => { void navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}>
        <Icon name={copied ? 'check' : 'copy'} />
      </Button>
    </div>
  );
}

// Key information, each edited in place: the stage through party.set_stage, the rest through party.update.
function KeyInfo({ ctx }: { ctx: Ctx }) {
  const { party, timeline, fields, users, save, editable } = ctx;
  const { me } = useSession();
  const phone = party.contact_points.find((c) => c.kind === 'phone');
  const address = party.contact_points.find((c) => c.kind === 'address');
  const last = [...(timeline?.activities ?? [])].map((a) => a.done_at ?? a.created_at).sort().pop();
  const lifecycle = lifecycleOf(fields, party.contact_class);
  const set = (patch: Record<string, unknown>) => save('party.update', { set: patch });
  const people = users.filter((u) => u.status === 'active' || u.id === party.owner_user_id);
  return (
    <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="key-h">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 id="key-h">Key information</h2>
        {editable && <span className="faint small">Changes save as you go</span>}
      </div>
      <dl className="key-info">
        <Fact label="Owner">
          <InlineSelect label="Owner" value={party.owner_user_id ?? ''} empty="Unassigned" disabled={!editable}
            options={people.map((u) => [u.id, u.id === me.user_id ? `${u.display_name} (you)` : u.display_name])} onSave={(v) => set({ owner_user_id: v || null })} />
        </Fact>
        <Fact label="Class">
          <InlineSelect label="Class" value={party.contact_class} disabled={!editable || !fields}
            options={(fields?.classes ?? []).map((c) => [c.key, c.name])} onSave={(v) => set({ contact_class: v })} />
        </Fact>
        <Fact label="Lifecycle stage">
          <InlineSelect label="Lifecycle stage" value={party.lifecycle_stage ?? ''} empty={NO_STAGE} disabled={!editable || !fields}
            options={(lifecycle?.stages ?? []).map((s) => [s.key, s.name])} onSave={(v) => save('party.set_stage', { stage: v || null })} />
        </Fact>
        <Fact label="Grade">
          <InlineSelect label="Grade" value={party.grade ?? ''} empty="No grade" disabled={!editable || !fields}
            options={(fields?.grades ?? []).map((g) => [g.key, gradeName(fields, g.key)])} onSave={(v) => set({ grade: v || null })} />
        </Fact>
        <Fact label="Phone">
          <InlineText label="Phone" type="tel" inputMode="tel" value={phone?.value.raw ?? ''} placeholder="Add a phone" validate={checkPhone} disabled={!editable}
            onSave={(v) => save('party.update', phone ? { contact_points_remove: [phone.id], ...pointAdd('phone', v, phone.label, phone.is_primary) } : pointAdd('phone', v, 'mobile', true))} />
        </Fact>
        <Fact label="City">
          <InlineText label="City" value={address?.value.city ?? ''} placeholder="Add a city" disabled={!editable} maxLength={100}
            onSave={(v) => save('party.update', address
              ? { contact_points_remove: [address.id], ...pointAdd('address', { ...addressOf(address.value), city: v }, address.label, address.is_primary) }
              : pointAdd('address', { ...addressOf({}), city: v }, 'home', true))} />
        </Fact>
        <Fact label="Tags">
          {editable ? <InlineTags label="Tags" value={party.tags} onSave={(next) => save('party.update', {
            ...(next.some((t) => !party.tags.includes(t)) ? { tags_add: next.filter((t) => !party.tags.includes(t)) } : {}),
            ...(party.tags.some((t) => !next.includes(t)) ? { tags_remove: party.tags.filter((t) => !next.includes(t)) } : {}),
          })} /> : party.tags.length ? <span className="row" style={{ gap: 4 }}>{party.tags.map((t) => <span key={t} className="tag">{t}</span>)}</span> : '–'}
        </Fact>
        <Fact label="Last activity">{last ? <Time iso={last} /> : '–'}</Fact>
      </dl>
      {party.tags.length > 0 && (
        <div className="row small" style={{ gap: 4 }}>
          <span className="faint">See contacts tagged</span>
          {party.tags.map((t) => <Link key={t} to={`/contacts?tag=${encodeURIComponent(t)}`} className="tag" aria-label={`Contacts tagged ${t}`}>{t}</Link>)}
        </div>
      )}
    </section>
  );
}

// What needs doing next and the latest of each kind of touch, drawn from records.
function Overview({ ctx, onAction }: { ctx: Ctx; onAction?: (a: Action) => void }) {
  const { timeline, threads, related, party } = ctx;
  const dups = useApi<{ candidates: Candidate[] }>(party.status === 'active' ? `/v1/parties/${party.id}/duplicates` : null, [party.id, party.version]);
  if (!timeline || !threads || !related) return <Loading label="Loading what is known" />;
  const now = Date.now();
  const nDups = dups.data?.candidates.length ?? 0;
  const open = timeline.activities.filter((a) => a.status === 'open').sort((a, b) => (a.due_at ?? '9').localeCompare(b.due_at ?? '9'));
  const openDeals = timeline.deals.filter((d) => d.status === 'open');
  const upcoming = related.events.filter((e) => new Date(e.starts_at).getTime() > now).sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const next: Array<{ key: string; tone: string; text: ReactNode; when?: string }> = [
    ...(nDups ? [{ key: 'dups', tone: 'warn', text: <>{nDups} possible duplicate{nDups === 1 ? '' : 's'} to review: <Link to="?tab=intelligence">see them in Intelligence</Link></> }] : []),
    ...open.map((a) => ({ key: a.id, tone: a.due_at && new Date(a.due_at).getTime() < now ? 'danger' : 'info', text: <>{human(a.kind)}: {a.subject}{a.due_at && new Date(a.due_at).getTime() < now ? <strong> (overdue)</strong> : null}</>, when: a.due_at ?? undefined })),
    ...upcoming.slice(0, 3).map((e) => ({ key: e.id, tone: 'info', text: <>Meeting: {e.title}</>, when: e.starts_at })),
  ];
  const lastEmail = threads[0];
  const lastActivity = [...timeline.activities].filter((a) => a.status === 'done').sort((a, b) => (b.done_at ?? b.created_at).localeCompare(a.done_at ?? a.created_at))[0];
  const lastNote = ctx.party.notes[0];
  return (
    <div className="stack">
      <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="next-h">
        <h2 id="next-h">Next up</h2>
        {next.length ? (
          <ul className="next-list">
            {next.map((n) => <li key={n.key} className={`next-${n.tone}`}><span className="grow">{n.text}</span>{n.when && <Time iso={n.when} />}</li>)}
          </ul>
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            <p className="muted" style={{ margin: 0 }}>Nothing is due and no meeting is booked.</p>
            {onAction && <div><Button type="button" variant="secondary" size="sm" onClick={() => onAction('task')}><Icon name="plus" />Add a task</Button></div>}
          </div>
        )}
      </section>
      <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="latest-h">
        <h2 id="latest-h">Latest</h2>
        <div className="latest-grid">
          <Latest label="Email" empty="No mail yet">{lastEmail && <><div className="item-title">{lastEmail.subject}</div><div className="item-sub">{lastEmail.last_direction === 'in' ? 'They wrote' : 'You wrote'} · <Time iso={lastEmail.last_message_at} /></div></>}</Latest>
          <Latest label="Activity" empty="No calls, meetings, or tasks done">{lastActivity && <><div className="item-title">{human(lastActivity.kind)}: {lastActivity.subject}</div><div className="item-sub"><Time iso={lastActivity.done_at ?? lastActivity.created_at} /></div></>}</Latest>
          <Latest label="Note" empty="No notes">{lastNote && <><div className="item-title clamp-2">{lastNote.body}</div><div className="item-sub"><Time iso={lastNote.created_at} /></div></>}</Latest>
        </div>
      </section>
      {openDeals.length > 0 && (
        <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="open-deals-h">
          <h2 id="open-deals-h">Open deals</h2>
          <ul className="plain-list">
            {openDeals.map((d) => <li key={d.id}><Link to={`/deals/${d.id}`}>{d.title}</Link> <span className="faint">· {d.stage_name} · {money(d.value_minor, d.currency)}{d.expected_close ? ` · closes ${day(d.expected_close)}` : ''}</span></li>)}
          </ul>
        </section>
      )}
    </div>
  );
}

function Latest({ label, empty, children }: { label: string; empty: string; children: ReactNode }) {
  return <div className="latest"><div className="inbox-row-topic">{label}</div>{children || <div className="muted small">{empty}</div>}</div>;
}

type Kind = 'all' | 'email' | 'meeting' | 'call' | 'task' | 'note' | 'change';
const HISTORY_OP: Record<string, string> = {
  create: 'Created', update: 'Edited', merge_survivor: 'Absorbed a duplicate', merged_into: 'Merged into another record',
  marked_distinct: 'Ruled not a duplicate', archive: 'Archived', bulk_edit: 'Bulk edited', restore: 'Restored',
};

// One timeline of everything that happened with the contact, newest first.
function Activities({ ctx }: { ctx: Ctx }) {
  const [kind, setKind] = useState<Kind>('all');
  const { me } = useSession();
  const { party, timeline, threads, related } = ctx;
  if (!timeline || !threads || !related) return <Loading label="Loading the timeline" />;
  type Item = { key: string; kind: Exclude<Kind, 'all'>; at: string; title: ReactNode; sub?: ReactNode; body?: string };
  const items: Item[] = [
    ...threads.map((t): Item => ({ key: `t${t.id}`, kind: 'email', at: t.last_message_at, title: t.subject, sub: `${t.messages} message${t.messages === 1 ? '' : 's'} · ${t.mailbox}` })),
    ...related.events.map((e): Item => ({ key: `e${e.id}`, kind: 'meeting', at: e.starts_at, title: e.title, sub: new Date(e.starts_at).getTime() > Date.now() ? 'Upcoming' : 'Met' })),
    ...timeline.activities.map((a): Item => ({
      key: `a${a.id}`, kind: a.kind === 'call' ? 'call' : a.kind === 'meeting' ? 'meeting' : a.kind === 'email' ? 'email' : a.kind === 'note' ? 'note' : 'task',
      at: a.done_at ?? a.due_at ?? a.created_at, title: `${human(a.kind)}: ${a.subject}`, sub: a.status === 'done' ? 'Done' : a.status === 'open' ? (a.due_at ? 'Due' : 'Open') : human(a.status),
    })),
    ...party.notes.map((n): Item => ({ key: `n${n.id}`, kind: 'note', at: n.created_at, title: 'Note', sub: noteAuthor(n.author, me.user_id), body: n.body })),
    ...party.history.map((h): Item => ({ key: `h${h.seq}`, kind: 'change', at: h.at, title: <Link to={`/activity/${h.proposal_id}`}>{HISTORY_OP[h.op] ?? human(h.op)}</Link>, sub: h.version ? `Version ${h.version}` : undefined })),
  ].sort((a, b) => b.at.localeCompare(a.at));
  const shown = kind === 'all' ? items : items.filter((i) => i.kind === kind);
  return (
    <div className="stack" style={{ gap: 12 }}>
      <ToggleGroup label="Show" value={kind} onChange={setKind} options={[['all', 'All'], ['email', 'Email'], ['meeting', 'Meetings'], ['call', 'Calls'], ['task', 'Tasks'], ['note', 'Notes'], ['change', 'Changes']]} />
      {shown.length === 0 ? <Empty title="Nothing here yet">Use the quick actions to log a call, add a note, or plan a task.</Empty> : (
        <ol className="cc-timeline" aria-label="Timeline">
          {shown.map((i) => (
            <li key={i.key} className={`cc-t-${i.kind}`}>
              <span className="cc-t-icon" aria-hidden="true"><Icon name={i.kind === 'email' ? 'mail' : i.kind === 'meeting' ? 'calendar' : i.kind === 'call' ? 'phone' : i.kind === 'note' ? 'note' : i.kind === 'change' ? 'activity' : 'task'} /></span>
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}><span className="item-title">{i.title}</span><span className="faint small"><Time iso={i.at} /></span></div>
                {i.sub && <div className="item-sub">{i.sub}</div>}
                {i.body && <NoteBody body={i.body} />}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function About({ ctx }: { ctx: Ctx }) {
  const { party, fields, save, editable } = ctx;
  const set = (patch: Record<string, unknown>) => save('party.update', { set: patch });
  const [adding, setAdding] = useState<Point['kind'] | 'occasion' | null>(null);
  const [occasion, setOccasion] = useState<{ kind: string; on_date: string }>({ kind: 'birthday', on_date: '' });
  const toast = useToast();
  const custom = (fields?.columns ?? []).filter((c) => c.key.startsWith('custom.')).map((c) => ({
    key: c.key.slice(7), label: c.label, type: c.type, options: (fields?.filters.find((f) => f.field === c.key)?.options as string[] | null) ?? [],
  }));
  const extra = Object.keys(party.custom).filter((k) => !custom.some((c) => c.key === k)).map((k) => ({ key: k, label: k.replace(/_/g, ' '), type: 'text', options: [] as string[] }));
  // A removal is asked once before it is proposed.
  const [removing, setRemoving] = useState<{ payload: Record<string, unknown>; what: string; value: string } | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const remove = async () => {
    if (!removing) return;
    setRemoveBusy(true);
    try { const note = await save('party.update', removing.payload)(); toast(note ?? `${removing.what} removed`); setRemoving(null); }
    catch (e) { toast((e as Error).message, 'error'); } finally { setRemoveBusy(false); }
  };
  const addOccasion = async () => {
    if (!occasion.on_date) return;
    try { const note = await save('party.update', { occasions_add: [occasion] })(); toast(note ?? 'Date added'); setAdding(null); setOccasion({ kind: 'birthday', on_date: '' }); }
    catch (e) { toast((e as Error).message, 'error'); }
  };
  const kinds: Array<[Point['kind'], string]> = [['email', 'Email'], ['phone', 'Phone'], ['address', 'Address']];
  return (
    <div className="stack">
      <section className={`${cardClass} card-pad stack`} style={{ gap: 12 }} aria-labelledby="about-details-h">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 id="about-details-h">Details</h2>
          {editable && <span className="faint small">Click a value to change it; it saves on its own</span>}
        </div>
        <dl className="facts">
          {party.kind === 'person' ? <>
            <Fact label="First name"><InlineText label="First name" value={party.given_name ?? ''} disabled={!editable} maxLength={100} onSave={(v) => set({ given_name: v || null })}
              validate={(v) => (!v && !party.family_name ? 'A person needs a first or last name.' : null)} /></Fact>
            <Fact label="Last name"><InlineText label="Last name" value={party.family_name ?? ''} disabled={!editable} maxLength={100} onSave={(v) => set({ family_name: v || null })}
              validate={(v) => (!v && !party.given_name ? 'A person needs a first or last name.' : null)} /></Fact>
          </> : (
            <Fact label={party.kind === 'household' ? 'Household name' : 'Organization name'}>
              <InlineText label="Name" value={party.org_name ?? ''} disabled={!editable} onSave={(v) => set({ org_name: v })} validate={(v) => (v ? null : 'Enter the name.')} />
            </Fact>
          )}
          <Fact label="Type">{human(party.kind)}</Fact>
          <Fact label="Visibility">
            <InlineSelect label="Visibility" value={party.visibility} disabled={!editable} onSave={(v) => set({ visibility: v })}
              options={[['workspace', 'Everyone in the workspace'], ['team', 'The owner’s team'], ['owner', 'Only the owner']]} />
          </Fact>
          <Fact label="Review">
            <InlineSelect label="Review" value={party.review_state} disabled={!editable} onSave={(v) => set({ review_state: v })}
              options={[['none', 'Not queued'], ['needs_review', 'Needs review'], ['reviewed', 'Reviewed']]} />
          </Fact>
        </dl>
      </section>
      <section className={`${cardClass} card-pad stack`} style={{ gap: 12 }} aria-labelledby="about-contact-h">
        <h2 id="about-contact-h">Contact</h2>
        {!party.contact_points.length && !adding && <p className="muted" style={{ margin: 0 }}>No email, phone, or address.</p>}
        <dl className="facts">
          {party.contact_points.map((c) => (
            <ContactPoint key={c.id} point={c} editable={editable} save={save} onRemove={() => setRemoving({ payload: { contact_points_remove: [c.id] }, what: human(c.kind), value: pointValue(c) })} />
          ))}
          {adding && adding !== 'occasion' && <NewPoint kind={adding} first={!party.contact_points.some((c) => c.kind === adding)} save={save} onDone={() => setAdding(null)} />}
        </dl>
        {editable && !adding && (
          <div className="row" style={{ gap: 6 }}>
            {kinds.map(([k, label]) => <Button key={k} type="button" variant="ghost" size="sm" onClick={() => setAdding(k)}><Icon name="plus" />{label}</Button>)}
          </div>
        )}
      </section>
      <section className={`${cardClass} card-pad stack`} style={{ gap: 12 }} aria-labelledby="about-dates-h">
        <h2 id="about-dates-h">Dates</h2>
        {!party.occasions.length && adding !== 'occasion' && <p className="muted" style={{ margin: 0 }}>No birthdays or anniversaries.</p>}
        <dl className="facts">
          {party.occasions.map((o) => (
            <Fact key={o.id} label={o.kind === 'home_anniversary' ? 'Home anniversary' : o.kind === 'birthday' ? 'Birthday' : o.label ?? 'Occasion'}>
              <span className="row" style={{ gap: 6 }}>
                {new Date(`${o.on_date}T00:00:00`).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
                {editable && <Button type="button" variant="ghost" size="sm" onClick={() => setRemoving({ payload: { occasions_remove: [o.id] }, what: 'Date', value: `${human(o.kind)}, ${o.on_date}` })}>Remove<span className="sr-only"> {human(o.kind)}</span></Button>}
              </span>
            </Fact>
          ))}
        </dl>
        {editable && (adding === 'occasion' ? (
          <div className="row" style={{ gap: 6 }}>
            <label className="sr-only" htmlFor="occ-kind">Kind of date</label>
            <select id="occ-kind" className={`${selectClass} h-9 w-auto`} value={occasion.kind} onChange={(e) => setOccasion({ ...occasion, kind: e.target.value })}>
              <option value="birthday">Birthday</option><option value="home_anniversary">Home anniversary</option><option value="other">Other</option>
            </select>
            <label className="sr-only" htmlFor="occ-date">Date</label>
            <input id="occ-date" className={`${fieldClass} h-9`} type="date" value={occasion.on_date} onChange={(e) => setOccasion({ ...occasion, on_date: e.target.value })} />
            <Button type="button" size="sm" disabled={!occasion.on_date} onClick={() => void addOccasion()}>Add date</Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(null)}>Cancel</Button>
          </div>
        ) : !adding && <div><Button type="button" variant="ghost" size="sm" onClick={() => setAdding('occasion')}><Icon name="plus" />Date</Button></div>)}
      </section>
      <ConfirmDialog open={!!removing} title={`Remove this ${removing?.what.toLowerCase() ?? ''}?`} confirmLabel="Remove" tone="danger" busy={removeBusy} onConfirm={() => void remove()} onCancel={() => setRemoving(null)}>
        <p style={{ margin: 0 }}>{removing?.value} comes off {party.display_name}. The history keeps it.</p>
      </ConfirmDialog>
      {(custom.length > 0 || extra.length > 0) && (
        <section className={`${cardClass} card-pad stack`} style={{ gap: 12 }} aria-labelledby="about-custom-h">
          <h2 id="about-custom-h">Custom fields</h2>
          <dl className="facts">
            {[...custom, ...extra].map((c) => {
              const raw = party.custom[c.key];
              const value = raw === null || raw === undefined ? '' : String(raw);
              const put = (v: string) => set({ custom: { [c.key]: v === '' ? null : c.type === 'number' ? Number(v) : c.type === 'boolean' ? v === 'true' : v } });
              return (
                <Fact key={c.key} label={c.label}>
                  {c.type === 'select' || c.type === 'boolean'
                    ? <InlineSelect label={c.label} value={value} empty="Not set" disabled={!editable} onSave={put}
                        options={c.type === 'boolean' ? [['true', 'Yes'], ['false', 'No']] : [...new Set([...c.options, ...(value ? [value] : [])])].map((o) => [o, o])} />
                    : <InlineText label={c.label} value={value} disabled={!editable} type={c.type === 'date' ? 'date' : 'text'} inputMode={c.type === 'number' ? 'decimal' : undefined}
                        validate={c.type === 'number' ? (v) => (v === '' || Number.isFinite(Number(v)) ? null : 'Enter a number.') : undefined} onSave={put} />}
                </Fact>
              );
            })}
          </dl>
        </section>
      )}
      <section className={`${cardClass} card-pad stack`} style={{ gap: 12 }} aria-labelledby="about-rel-h">
        <h2 id="about-rel-h">Relationships</h2>
        {party.relationships.length ? (
          <div className="list" style={{ margin: '0 -20px' }}>
            {party.relationships.map((r) => {
              const [outLabel, inLabel] = REL_LABEL[r.kind] ?? [r.kind, r.kind];
              const hidden = isHiddenParty(r.other_name);
              const body = <div className="grow"><div className={`item-title${hidden ? ' muted' : ''}`}>{r.other_name}</div><div className="item-sub">{r.direction === 'out' ? outLabel : inLabel}{r.role ? ` · ${r.role.replace('_', ' ')}` : ''}</div></div>;
              // A related party you may not see has no page to open.
              return hidden
                ? <div key={r.id} className="list-item">{body}</div>
                : <Link key={r.id} to={`/contacts/${r.other_party_id}`} className="list-item">{body}</Link>;
            })}
          </div>
        ) : <p className="muted" style={{ margin: 0 }}>No relationships yet.</p>}
      </section>
      <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="about-record-h">
        <h2 id="about-record-h">Record</h2>
        <p className="faint small" style={{ margin: 0 }}>Facts the system keeps: they change only by their own actions.</p>
        <dl className="facts small">
          <Fact label="Source">{[party.source.channel, party.source.system && systemName(party.source.system)].filter(Boolean).join(' · ') || 'Entered here'}</Fact>
          <Fact label="Created">{new Date(party.created_at).toLocaleDateString()}</Fact>
          <Fact label="Version">{party.version}</Fact>
          {Object.entries(party.external_ids).map(([sys, ids]) => <Fact key={sys} label={`ID in ${systemName(sys)}`}><span className="mono">{ids.join(', ')}</span></Fact>)}
          {party.parent_party_id && <Fact label="Parent"><PartyName id={party.parent_party_id} /></Fact>}
          <Fact label="Updated">{new Date(party.updated_at).toLocaleDateString()}</Fact>
        </dl>
      </section>
      <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="about-history-h">
        <h2 id="about-history-h">History</h2>
        <p className="faint" style={{ margin: 0 }}>Every change came through the gate. Open one to see why it was allowed.</p>
        <ol className="timeline" reversed>
          {party.history.map((h) => (
            <li key={h.seq} className={h.op.startsWith('merge') ? 'warn' : 'ok'}>
              <Link to={`/activity/${h.proposal_id}`}>{HISTORY_OP[h.op] ?? h.op.replace(/_/g, ' ')}</Link>
              {h.version ? <span className="faint"> · version {h.version}</span> : null}
              <div className="faint"><Time iso={h.at} /></div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function Revenue({ ctx }: { ctx: Ctx }) {
  const { timeline, related } = ctx;
  if (!timeline || !related) return <Loading label="Loading deals and quotes" />;
  return (
    <div className="stack">
      <section className={`${cardClass} stack`} style={{ gap: 0 }} aria-labelledby="rev-deals-h">
        <div className="card-head"><h2 id="rev-deals-h">Deals</h2></div>
        {timeline.deals.length === 0 ? <Empty title="No deals yet" /> : (
          <Table><caption className="sr-only">Deals with this contact</caption>
            <thead><tr><th scope="col">Deal</th><th scope="col">Stage</th><th scope="col">Value</th><th scope="col">Closes</th></tr></thead>
            <tbody>{timeline.deals.map((d) => <tr key={d.id}><td><Link to={`/deals/${d.id}`}>{d.title}</Link></td><td>{d.status === 'open' ? d.stage_name : human(d.status)}</td><td className="num-cell">{money(d.value_minor, d.currency)}</td><td>{day(d.expected_close)}</td></tr>)}</tbody>
          </Table>
        )}
      </section>
      <section className={`${cardClass} stack`} style={{ gap: 0 }} aria-labelledby="rev-quotes-h">
        <div className="card-head"><h2 id="rev-quotes-h">Quotes</h2></div>
        {related.quotes.length === 0 ? <Empty title="No quotes yet" /> : (
          <Table><caption className="sr-only">Quotes for this contact</caption>
            <thead><tr><th scope="col">Quote</th><th scope="col">Status</th><th scope="col">Total</th><th scope="col">Created</th></tr></thead>
            <tbody>{related.quotes.map((q) => <tr key={q.id}><td><Link to={`/quotes/${q.id}`}>{q.number} v{q.version}</Link></td><td>{human(q.status)}</td><td className="num-cell">{money(q.total_minor, q.currency)}</td><td>{day(q.created_at)}</td></tr>)}</tbody>
          </Table>
        )}
      </section>
      {timeline.leads.length > 0 && (
        <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="rev-leads-h">
          <h2 id="rev-leads-h">Leads</h2>
          <ul className="plain-list">{timeline.leads.map((l) => <li key={l.id}>{l.title} <span className="faint">· {human(l.status)}{l.grade ? ` · grade ${l.grade}` : ''} · {day(l.created_at)}</span></li>)}</ul>
        </section>
      )}
    </div>
  );
}

interface Memory { id: string; topic: string; claim: string; status: string; captured_by: string; created_at: string }
interface Touches { touches: Array<{ id: string; channel: string; source_ref: string; occurred_at: string }>; conversions: Array<{ id: string; kind: string; ref: string; occurred_at: string }> }

// Duplicates, what agents learned about the contact, and marketing touches.
function Intelligence({ party, onChanged }: { party: Party; onChanged: () => void }) {
  const memory = useApi<Memory[]>(`/v1/memory?identity=${encodeURIComponent(`party:${party.id}`)}&limit=50`, [party.id]);
  const touches = useApi<Touches>(`/v1/parties/${party.id}/touches`, [party.id]);
  const claims = (memory.data ?? []).filter((m) => m.status !== 'retired');
  return (
    <div className="stack">
      {party.status === 'active' ? <Duplicates party={party} onChanged={onChanged} /> : null}
      <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="intel-mem-h">
        <h2 id="intel-mem-h">What agents have learned</h2>
        <p className="faint small" style={{ margin: 0 }}>Claims agents captured about this contact. Captured ones are unconfirmed until a person promotes them to canon.</p>
        {memory.error ? <ErrorBanner error={memory.error} /> : !memory.data ? <Loading /> : claims.length === 0 ? <p className="muted" style={{ margin: 0 }}>Nothing yet.</p> : (
          <ul className="plain-list">
            {claims.map((m) => <li key={m.id}><Badge tone={m.status === 'canon' ? 'success' : 'neutral'}>{m.status === 'canon' ? 'Canon' : 'Captured'}</Badge> <strong>{human(m.topic)}</strong>: {m.claim} <span className="faint small">· <Time iso={m.created_at} /></span></li>)}
          </ul>
        )}
      </section>
      <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="intel-touch-h">
        <h2 id="intel-touch-h">Marketing touches</h2>
        {touches.error ? <ErrorBanner error={touches.error} /> : !touches.data ? <Loading /> : touches.data.touches.length + touches.data.conversions.length === 0 ? <p className="muted" style={{ margin: 0 }}>No touches or conversions recorded.</p> : (
          <ul className="plain-list">
            {touches.data.conversions.map((c) => <li key={c.id}><Badge tone="success">Conversion</Badge> {human(c.kind)} · <code>{c.ref}</code> <span className="faint small">· <Time iso={c.occurred_at} /></span></li>)}
            {touches.data.touches.slice(0, 20).map((t) => <li key={t.id}>{human(t.channel)} · <code>{t.source_ref}</code> <span className="faint small">· <Time iso={t.occurred_at} /></span></li>)}
          </ul>
        )}
      </section>
    </div>
  );
}

function RelatedCard({ title, count, children }: { title: string; count: number | undefined; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  const id = useId();
  return (
    <section className={`${cardClass} related-card`} aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} className="related-head">
        <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
          <span className={`nav-caret${open ? ' open' : ''}`}><Icon name="down" /></span>{title}{count !== undefined && <span className="tree-count">{count}</span>}
        </button>
      </h2>
      <div id={id} hidden={!open} className="related-body">
        {count === undefined ? <Loading /> : count === 0 ? <p className="muted small" style={{ margin: 0 }}>None yet.</p> : <ul className="plain-list">{children}</ul>}
      </div>
    </section>
  );
}

function RelatedRow({ to, title, sub }: { to?: string; title: string; sub: string }) {
  return <li>{to ? <Link to={to}>{title}</Link> : <span className="muted">{title}</span>}<div className="item-sub">{sub}</div></li>;
}

// The quick actions: each a short form in a dialog that proposes the change.
function QuickAction({ kind, party, onClose, onDone }: { kind: Action; party: Party; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const emails = party.contact_points.filter((c) => c.kind === 'email').map((c) => c.value.raw as string);
  const boxes = useApi<Array<{ kind: string; mine: boolean; connected: unknown[] }>>(kind === 'email' ? '/v1/mailboxes' : null);
  const first = party.given_name ?? party.display_name;
  const [subject, setSubject] = useState(kind === 'call' ? `Call with ${first}` : kind === 'meeting' ? `Meeting with ${first}` : kind === 'task' ? `Follow up with ${first}` : '');
  const [body, setBody] = useState('');
  const [when, setWhen] = useState('');
  const [to, setTo] = useState(emails[0] ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const title = { note: 'Add a note', email: `Email ${party.display_name}`, call: 'Log a call', task: 'Plan a task', meeting: 'Log or plan a meeting' }[kind];
  const confirm = { note: 'Add note', email: 'Send email', call: 'Log call', task: 'Add task', meeting: when && new Date(when).getTime() > Date.now() ? 'Plan meeting' : 'Log meeting' }[kind];
  async function submit() {
    setError(null);
    if (kind === 'note' ? !body.trim() : !subject.trim()) { setError(new Error(kind === 'note' ? 'Write the note first.' : 'Give it a subject.')); return; }
    if (kind === 'task' && !when) { setError(new Error('Say when it is due.')); return; }
    setBusy(true);
    try {
      const at = when ? new Date(when).toISOString() : undefined;
      const out = kind === 'note' ? await propose('note.create', { party_id: party.id, body: body.trim() })
        : kind === 'email' ? await propose('comms.email_send', {
          party_id: party.id, to, subject: subject.trim(), body_text: body.trim(), category: 'one_to_one',
          mailbox: boxes.data?.some((b) => b.kind === 'user' && b.mine && b.connected.length) ? 'user' : 'workspace',
        })
        : await propose('activity.create', {
          kind, subject: subject.trim(), party_id: party.id, ...(body.trim() ? { body: body.trim() } : {}),
          ...(kind === 'call' ? { done: true } : kind === 'task' ? { due_at: at } : at && new Date(at).getTime() > Date.now() ? { due_at: at } : { done: true }),
        });
      const saved = { note: 'Note added', email: out.state === 'held' ? 'Email queued: it goes out when quiet hours end' : 'Email sent', call: 'Call logged', task: 'Task added', meeting: 'Meeting saved' }[kind];
      if (out.state === 'rejected') { setError(new Error(outcomeMessage(out))); return; }
      toast(outcomeMessage(out, saved), outcomeTone(out));
      onDone(); onClose();
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  return (
    <ConfirmDialog open title={title} confirmLabel={confirm} busy={busy} onConfirm={() => void submit()} onCancel={onClose}>
      <div className="stack" style={{ gap: 10 }}>
        <ErrorBanner error={error} />
        {kind === 'email' && (
          <Field label="To"><select className={selectClass} value={to} onChange={(e) => setTo(e.target.value)}>{emails.map((e) => <option key={e}>{e}</option>)}</select></Field>
        )}
        {kind !== 'note' && <Field label="Subject" required><input className={fieldClass} value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} /></Field>}
        {(kind === 'task' || kind === 'meeting') && (
          <Field label={kind === 'task' ? 'Due' : 'When'} required={kind === 'task'} hint={kind === 'meeting' ? 'A time ahead plans it; a time past, or none, logs it as held.' : undefined}>
            <input className={fieldClass} type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
          </Field>
        )}
        <Field label={kind === 'note' ? 'Note' : kind === 'email' ? 'Message' : kind === 'call' ? 'What was said' : 'Details'} hint={kind === 'note' ? 'Saved as plain text.' : 'Optional.'} required={kind === 'note'}>
          <textarea className={textareaClass} rows={kind === 'email' ? 6 : 3} value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
      </div>
    </ConfirmDialog>
  );
}

function pointValue(c: Point) { return c.kind === 'address' ? formatAddress(c.value) : String(c.value.raw ?? ''); }

// One email, phone, or address: its value and label edited in place, or removed.
function ContactPoint({ point: c, editable, save, onRemove }: { point: Point; editable: boolean; save: Saver; onRemove: () => void }) {
  const change = (value: unknown, label = c.label) => save('party.update', { contact_points_remove: [c.id], ...pointAdd(c.kind, value, label, c.is_primary) });
  const name = human(c.kind);
  return (
    <>
      <dt>{name}{c.is_primary ? ' · primary' : ''}</dt>
      <dd className="point-row">
        {c.kind === 'address' ? (
          <InlineGroup label={`${name} ${pointValue(c)}`} value={addressOf(c.value)} display={pointValue(c) || 'Add the address'} validate={checkAddress}
            fields={[{ key: 'street', label: 'Street' }, { key: 'city', label: 'City', width: 140 }, { key: 'state', label: 'State', width: 56 }, { key: 'postal_code', label: 'ZIP', width: 90, inputMode: 'numeric' }]}
            onSave={(a) => change(a)} />
        ) : editable ? (
          <InlineText label={name} value={pointValue(c)} type={c.kind === 'email' ? 'email' : 'tel'} inputMode={c.kind === 'email' ? 'email' : 'tel'}
            validate={c.kind === 'email' ? checkEmail : checkPhone} onSave={(v) => change(v)} maxLength={c.kind === 'email' ? 320 : 32} />
        ) : <a href={`${c.kind === 'email' ? 'mailto' : 'tel'}:${c.kind === 'email' ? c.value.raw : c.normalized ?? c.value.raw}`}>{pointValue(c)}</a>}
        {editable && <>
          <InlineSelect label={`${name} label`} value={c.label} options={[...new Set([...LABELS[c.kind], c.label])].map((l) => [l, human(l)])}
            onSave={(l) => change(c.kind === 'address' ? addressOf(c.value) : c.value.raw, l)} />
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>Remove<span className="sr-only"> {name} {pointValue(c)}</span></Button>
        </>}
      </dd>
    </>
  );
}

function NewPoint({ kind, first, save, onDone }: { kind: Point['kind']; first: boolean; save: Saver; onDone: () => void }) {
  const label = LABELS[kind][0]!;
  const name = `New ${kind}`;
  return (
    <>
      <dt>{human(kind)}</dt>
      <dd className="point-row">
        {kind === 'address' ? (
          <InlineGroup startOpen label={name} value={addressOf({})} display="" validate={checkAddress} onCancel={onDone}
            fields={[{ key: 'street', label: 'Street' }, { key: 'city', label: 'City', width: 140 }, { key: 'state', label: 'State', width: 56 }, { key: 'postal_code', label: 'ZIP', width: 90, inputMode: 'numeric' }]}
            onSave={(a) => async () => { const note = await save('party.update', pointAdd('address', a, label, first))(); onDone(); return note; }} />
        ) : (
          <NewText kind={kind} onCancel={onDone} onSave={(v) => async () => { const note = await save('party.update', pointAdd(kind, v, label, first))(); onDone(); return note; }} />
        )}
      </dd>
    </>
  );
}

function NewText({ kind, onSave, onCancel }: { kind: 'email' | 'phone'; onSave: (v: string) => Save; onCancel: () => void }) {
  const [v, setV] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Disabling the input on Enter blurs it; the blur must not send it again.
  const sent = useRef(false);
  const id = useId();
  const submit = async () => {
    if (sent.current) return;
    const t = v.trim();
    if (!t) { onCancel(); return; }
    const p = kind === 'email' ? checkEmail(t) : checkPhone(t);
    if (p) { setProblem(p); return; }
    sent.current = true;
    setBusy(true);
    try { await onSave(t)(); } catch (e) { sent.current = false; setProblem((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <span className="inline-edit editing">
      <label className="sr-only" htmlFor={id}>{`New ${kind}`}</label>
      <input id={id} className={`${fieldClass} h-9`} type={kind === 'email' ? 'email' : 'tel'} autoFocus value={v} disabled={busy} aria-invalid={problem ? true : undefined} aria-describedby={problem ? `${id}-e` : undefined}
        onChange={(e) => { setV(e.target.value); setProblem(null); }} onBlur={() => void submit()}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } if (e.key === 'Escape') onCancel(); }} />
      {problem ? <span id={`${id}-e`} className="save-state error" role="alert">{problem}</span> : <span className="save-state" role="status" aria-live="polite">{busy ? 'Saving…' : ''}</span>}
    </span>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return <><dt>{label}</dt><dd>{children}</dd></>;
}

function OwnerName({ id }: { id: string }) {
  const { me } = useSession();
  const users = useApi<Array<{ id: string; display_name: string }>>('/v1/users');
  if (id === me.user_id) return <>You</>;
  return <>{users.data?.find((u) => u.id === id)?.display_name ?? '…'}</>;
}

function Duplicates({ party, onChanged }: { party: Party; onChanged: () => void }) {
  const { data, loading, error: loadError, reload } = useApi<{ candidates: Candidate[] }>(`/v1/parties/${party.id}/duplicates`, [party.id, party.version]);
  const toast = useToast();
  const navigate = useNavigate();
  const { refreshStats } = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const [distinctFor, setDistinctFor] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [confirm, setConfirm] = useState<{ c: Candidate; survivor: Party; merged: Party } | null>(null);
  const triggers = useRef<Record<string, HTMLButtonElement | null>>({});
  const mergeButtons = useRef<Record<string, HTMLButtonElement | null>>({});

  if (loading && !data) return null;
  if (loadError) return <div className={`${cardClass} card-pad`}><h2>Possible duplicates</h2><p className="small" role="alert" style={{ margin: '6px 0 0', color: 'var(--danger)' }}>Could not check for duplicates. <Button type="submit" variant="ghost" size="sm" onClick={() => void reload()}>Try again</Button></p></div>;
  const candidates = data?.candidates ?? [];
  if (!candidates.length) return null;

  // Load both records and ask before proposing; the older record survives.
  async function askMerge(c: Candidate) {
    setBusy(c.party_id); setError(null);
    try {
      const other = await api<Party>(`/v1/parties/${c.party_id}`);
      const [survivor, merged] = new Date(party.created_at) <= new Date(other.created_at) ? [party, other] : [other, party];
      setConfirm({ c, survivor, merged });
    } catch (e) { setError(e); } finally { setBusy(null); }
  }

  async function merge() {
    if (!confirm) return;
    const { c, survivor, merged } = confirm;
    setBusy(c.party_id); setError(null);
    try {
      const out = await propose('party.merge', {
        survivor_id: survivor.id, merged_id: merged.id,
        survivor_base_version: survivor.version, merged_base_version: merged.version,
        explanation: `Merged by a person from the duplicates panel (score ${c.score}).`,
      }, `Merge proposed from the duplicates panel. Engine: ${c.level} (${c.reasons.join(', ')})`);
      toast(outcomeMessage(out, 'Merge saved. Nothing was deleted.'), outcomeTone(out));
      forgetParty(c.party_id);
      refreshStats();
      setConfirm(null);
      if (out.state === 'written' && survivor.id !== party.id) navigate(`/contacts/${survivor.id}`);
      else { onChanged(); void reload(); }
    } catch (e) { setError(e); setConfirm(null); } finally { setBusy(null); }
  }

  function cancelDistinct(id: string) { setDistinctFor(null); setReason(''); triggers.current[id]?.focus(); }

  async function markDistinct(e: FormEvent, c: Candidate) {
    e.preventDefault();
    setBusy(c.party_id); setError(null);
    try {
      const out = await propose('party.mark_distinct', { party_ids: [party.id, c.party_id], reason: reason || 'Different people' }, reason);
      toast(outcomeMessage(out, 'Marked as different people'), outcomeTone(out));
      setDistinctFor(null); setReason('');
      refreshStats();
      void reload();
    } catch (err) { setError(err); } finally { setBusy(null); }
  }

  return (
    <section className={cardClass} aria-labelledby="dup-h">
      <div className="card-head">
        <div><h2 id="dup-h">Possible duplicates</h2><div className="faint">Found by the dedup engine. You decide, or let the Merge reviewer agent propose.</div></div>
      </div>
      <ErrorBanner error={error} />
      <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {candidates.map((c) => (
          <li key={c.party_id} className="list-item" style={{ flexWrap: 'wrap' }}>
            <div className="grow">
              <div className="item-title"><PartyName id={c.party_id} /></div>
              <div className="item-sub">{c.reasons.join(' · ')}</div>
            </div>
            <Badge tone={c.level === 'duplicate' ? 'danger' : 'warning'}>{c.level === 'duplicate' ? 'Likely duplicate' : 'Possible'}</Badge>
            <Score value={c.score} />
            {distinctFor === c.party_id ? (
              <form className="stack" style={{ width: '100%', gap: 8 }} onSubmit={(e) => markDistinct(e, c)} onKeyDown={(e) => { if (e.key === 'Escape') cancelDistinct(c.party_id); }}>
                <Field label="What tells them apart?" hint="For example: spouses, or a parent and child with the same name.">
                  <input className={fieldClass} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
                </Field>
                <div className="row">
                  <Button type="submit" size="sm" disabled={busy === c.party_id}>Propose: not the same</Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => cancelDistinct(c.party_id)}>Cancel</Button>
                </div>
              </form>
            ) : (
              <div className="row" style={{ gap: 6 }}>
                <Button type="submit" ref={(el) => { mergeButtons.current[c.party_id] = el; }} variant="secondary" size="sm" disabled={!!busy} onClick={() => askMerge(c)}><Icon name="merge" />{busy === c.party_id ? 'Loading…' : 'Merge…'}</Button>
                <Button type="submit" ref={(el) => { triggers.current[c.party_id] = el; }} variant="ghost" size="sm" disabled={!!busy} onClick={() => setDistinctFor(c.party_id)}>Not the same</Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <ConfirmDialog open={!!confirm} title="Propose this merge?" confirmLabel="Propose merge" busy={!!busy} onConfirm={merge} onCancel={() => setConfirm(null)} returnFocus={confirm ? mergeButtons.current[confirm.c.party_id] : null}>
        {confirm && <>
          <p style={{ margin: 0 }}><strong>{confirm.survivor.display_name}</strong> stays (the older record). <strong>{confirm.merged.display_name}</strong> folds into it and is kept with the status merged. Nothing is deleted.</p>
          <div className="compare">
            {([['Keeps', confirm.survivor], ['Folds in', confirm.merged]] as const).map(([label, p]) => (
              <div key={label}>
                <div className="faint">{label}</div>
                <strong>{p.display_name}</strong>
                {p.contact_points.filter((c) => c.kind !== 'address').slice(0, 3).map((c) => <div key={c.id} className="small">{c.value.raw}</div>)}
                <div className="faint">Created {new Date(p.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
          <p className="muted small" style={{ margin: 0 }}>This is a proposal. The gate saves it now or sends it to Approvals if a person must decide. Engine: {confirm.c.level}, score {confirm.c.score.toFixed(2)}.</p>
        </>}
      </ConfirmDialog>
    </section>
  );
}

function noteAuthor(author: string, myId: string) {
  if (author === `user:${myId}`) return 'You';
  if (author.startsWith('agent:')) return `Agent ${author.split('/').pop()?.replace(/-/g, ' ')}`;
  if (author.startsWith('user:')) return 'A teammate';
  return 'Imported';
}

// Long notes collapse to a preview. Record text is data: rendered as text, never markup.
function NoteBody({ body }: { body: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const long = body.length > 600 || body.split('\n').length > 8;
  if (!long) return <div className="note">{body}</div>;
  return (
    <div className="stack" style={{ gap: 4 }}>
      <div className="note" id={id}>{open ? body : `${body.slice(0, 400).trimEnd()}…`}</div>
      <Button variant="ghost" size="sm" press="tint" className="self-start px-0 [&_svg]:transition-transform aria-expanded:[&_svg]:rotate-90" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}><Icon name="chevron" />{open ? 'Show less' : 'Show the full note'}</Button>
    </div>
  );
}

interface ConsentView { email: { may_send: boolean; latest: { action: string; legal_basis: string; at: string } | null }; subscriptions: Array<{ key: string; name: string; category: string; status: string }>; entries: Array<{ id: string; channel: string; subscription_type: string | null; action: string; legal_basis: string; source: { kind: string; detail?: string }; at: string; proposal_id: string | null }> }

// Consent: what may be sent to this person now, their subscriptions, and the
// append-only ledger behind it. Changing it is a proposal.
function Consent({ partyId }: { partyId: string }) {
  const c = useApi<ConsentView>(`/v1/parties/${partyId}/consent`, [partyId]);
  const toast = useToast();
  const d = c.data;
  const [changing, setChanging] = useState<string | null>(null);
  async function change(key: string, status: 'opted_in' | 'opted_out') {
    setChanging(key);
    try {
      const out = await propose('subscription.change', { party_id: partyId, subscription_type: key, status, ...(status === 'opted_in' ? { legal_basis: 'consent' } : {}), source: { kind: 'person', detail: 'Changed on the contact page' } });
      toast(outcomeMessage(out, status === 'opted_in' ? 'Subscribed' : 'Unsubscribed'), outcomeTone(out)); await c.reload();
    } catch (e) { toast((e as Error).message, 'error'); } finally { setChanging(null); }
  }
  return (
    <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="consent-h">
      <h2 id="consent-h">Consent</h2>
      {!d ? <ErrorBanner error={c.error} /> : <>
        <Alert role="note" tone={d.email.may_send ? 'success' : 'warning'}>{d.email.may_send ? `Email may be sent (${d.email.latest?.legal_basis.replace(/_/g, ' ')}).` : d.email.latest ? `No email: ${d.email.latest.action.replace(/_/g, ' ')}.` : 'No email consent recorded.'}</Alert>
        <ul className="list" style={{ listStyle: 'none', margin: '0 -20px', padding: 0 }}>
          {d.subscriptions.map((s) => (
            <li key={s.key} className="list-item">
              <div className="grow"><div className="item-title">{s.name}</div><div className="item-sub">{s.category} · {s.status.replace(/_/g, ' ')}</div></div>
              {s.category !== 'transactional' && (s.status === 'opted_in'
                ? <Button type="submit" variant="ghost" size="sm" disabled={changing === s.key} onClick={() => void change(s.key, 'opted_out')}>Unsubscribe<span className="sr-only"> from {s.name}</span></Button>
                : <Button type="submit" variant="secondary" size="sm" disabled={changing === s.key} onClick={() => void change(s.key, 'opted_in')}>Subscribe<span className="sr-only"> to {s.name}</span></Button>)}
            </li>
          ))}
        </ul>
        {d.entries.length > 0 && (
          <details><summary className="small" style={{ cursor: 'pointer' }}>Consent ledger ({d.entries.length})</summary>
            <ol className="timeline" style={{ marginTop: 8 }}>{d.entries.map((e) => <li key={e.id}>{e.action.replace(/_/g, ' ')} · {e.subscription_type ?? e.channel} · {e.source.kind}<div className="faint"><Time iso={e.at} /></div></li>)}</ol>
          </details>
        )}
      </>}
    </section>
  );
}
